/*
 * STANDARD-STUDIO: Spoke Color Studio for STANDARD builds (steel and Berd spokes) in the LoamLabs Custom Wheel Builder.
 *
 * Plain JavaScript, no libraries, no module syntax. The only global is window.StandardStudio.
 * Loaded after variant-builder.js, spoke-studio.js and i9-system.js. Same pattern as assets/i9-system.js: it reads builder state and
 * caches through window.__loamBuilderInternals and is called from small hooks in variant-builder.js / cart.js, each marked
 * "// STANDARD-STUDIO:". For a System build, or when a spoke product has fewer than two colors, every entry point is a no-op.
 *
 * Owns: the per-wheel "Color each spoke individually" card in Step 3, the single-wheel Studio launcher, the custom-painting state and its
 * repair rules, the per-wheel fee ("Custom Spoke Color Configuration": Mixed colors / Berd painted color / Berd multi-color), the readable
 * order properties and the _build.standardStudio contract the backend reads.
 *
 * Cart model: a Standard spoke line is ONE line per wheel (quantity 1, the list color's "-" variant, priced per wheel). A Studio wheel keeps
 * that line; the colors travel in properties and _build.standardStudio, and the fee is its own line (rides in {pos}RequiredAddons).
 */
(function (global) {
  'use strict';

  /* ------------------------------------------------------------------ configuration (edit here) */
  var CFG = {
    ignoreColors: ['Custom Configuration'],            // a Color value that is never offered (being deleted in Shopify)
    minColors: 2,                                      // a spoke product needs this many usable colors to get the Studio
    // ONE name-to-hex list for spoke and hub colors. A color that is not here still works (gray swatch + name). The Berd and steel values come
    // from Agent A's sandbox; the hub color values are first guesses. Jerry corrects them here, nowhere else.
    colorHex: {
      Black: '#1d1f23', Silver: '#c3c8cf',
      'Black Berd': '#1d1f23', 'White Berd': '#f2f2f2', 'Blue Berd': '#1f62d6', 'Flamingo Pink': '#ee5ba3', 'Mallard Green': '#1e6b4a',
      'Martin Purple': '#7b3fb9', 'Oriole Orange': '#f07a1a', 'Robin Red': '#d0262b',
      // hub colors (guesses)
      '3d Violet': '#8a4fff', 'Antifreeze Green': '#7ed957', 'Baby Blue': '#89cff0', 'Black (Anodized)': '#1d1f23', 'Black (Matte)': '#26282c',
      'Black Aura': '#2a2d33', Blue: '#1f62d6', 'Blue (Anodized)': '#1f62d6', 'British Racing Green': '#004225', Bronze: '#8d6a3e',
      'Candy Blue': '#1b6fd6', 'Candy Purple': '#7a2fc0', 'Candy Red': '#b01025', 'Cardinal Yellow': '#f5d90a', Cobalt: '#0047ab',
      'Dark Blue': '#14305f', 'Dark Green': '#14532d', 'Flat Earth': '#8c7b66', 'Frost White': '#eef1f5', Gold: '#dcae2a',
      'Gold (Anodized)': '#dcae2a', Green: '#2f9e48', 'Green (Anodized)': '#2f9e48', 'Hi-Vis Green': '#7cff2f', 'Hi-Vis Orange': '#ff6a00',
      'Hi-Vis Pink': '#ff4fa6', 'Hi-Vis Yellow': '#e8ff3a', Jade: '#00a86b', 'Kawasaki Green': '#6bbf23', Lime: '#a5d12b', Maple: '#a9703d',
      'Matte Black (Anodized)': '#26282c', 'Matte Gold 50th': '#c9a227', 'Matte Mango': '#ff9e2c', Midnight: '#191970', Orange: '#f07a1a',
      'Orange (Anodized)': '#f07a1a', Pink: '#ee5ba3', 'Pink (Anodized)': '#ee5ba3', Purple: '#7b3fb9', 'Purple (Anodized)': '#7b3fb9',
      Red: '#d0262b', 'Red (Anodized)': '#d0262b', Ruby: '#9b111e', 'Silver (Anodized)': '#c3c8cf', Smoke: '#7d848c', Turquoise: '#20b2aa',
      'Turquoise (Anodized)': '#20b2aa', 'Turquoise (Matte)': '#20b2aa', 'Two Tone Black Punch': '#2b2d31', Ultraviolet: '#5b2a86'
    },
    neutralHub: { name: 'Silver', hex: '#c3c8cf' },     // the hub color shown when no hub is chosen yet
    crossCapFrom: 28,                                  // from this many holes the "largest possible" crossing is capped at 3 (see crossFor)
    defaultPcd: 58,                                   // mm, used when a hub has no flange diameter metafield
    defaultErd: 600,                                   // mm, used when the rim has no rim_erd
    // Which physical side is "drive" and "rotor" in the Studio, and the words shown (the order property names stay Drive / Rotor).
    sideLabels: { rear: { drive: 'Drive side', rotor: 'Non-drive side' }, front: { drive: 'Right side', rotor: 'Left side' } },
    paint: { blank: 'White Berd' },                    // _build.standardStudio.paint.blank: the color that is bought and painted
    fee: {
      name: 'Custom Spoke Color Configuration',
      optionName: 'Fee Type',                          // the fee product's variants are found by this option's VALUE, never by price
      // gid / cents are fallbacks used only when the Storefront lookup fails; the lookup replaces them by option value.
      types: {
        mixed:   { value: 'Mixed colors',       gid: 'gid://shopify/ProductVariant/67791816196403', cents: 2500 },
        painted: { value: 'Berd painted color', gid: 'gid://shopify/ProductVariant/67803454931251', cents: 3500 },
        multi:   { value: 'Berd multi-color',   gid: 'gid://shopify/ProductVariant/67803454964019', cents: 5000 }
      }
    },
    // The fee explanation the Studio's info popup shows (config.feeInfo). Each row names the example that produces its fee TYPE through
    // feeTypeFor (the cart's own rule), so the amount shown is the amount the cart charges; amounts come from the fee variants' prices.
    // "P1" / "P2" stand for painted colors, "B" / "W" for the colors that are not painted (Black Berd, White Berd).
    feeInfo: {
      title: 'Custom spoke color fee',
      note: 'The fee is charged once per wheel.',
      idle: 'No color fee yet',          // the fee box text before any fee applies (the Studio shows the box, with its "i", so the popup can be opened early)
      // The row ORDER is the popup's order: the no-fee row first, then rising price.
      steel: [
        { label: 'One color on a wheel', when: 'No fee.', example: ['A'] },
        { label: 'Two or more colors on a wheel', when: 'Any mix, for example black and silver.', example: ['A', 'B'] }
      ],
      painted: [
        { label: 'Solid black or solid white', when: 'No fee.', example: ['B'] },
        { label: 'Black and white mixed', when: 'Only Black Berd and White Berd on the same wheel.', example: ['B', 'W'] },
        { label: 'One painted color', when: 'One color other than black or white, alone or together with black or white. Each spoke is hand-painted.', example: ['B', 'P1'] },
        { label: 'Two or more painted colors', when: 'Two or more colors other than black or white on the same wheel.', example: ['P1', 'P2'] }
      ]
    },
    text: {
      cardTitle: 'Color each spoke individually',
      cardButton: 'Open Spoke Color Studio',
      replacedNotice: 'Some saved spoke colors are no longer available and were reset to the base color.',
      resetNotice: 'Your spoke count, lacing or hub changed, so the custom spoke colors on the {wheel} wheel were reset to the base color.'
    }
  };

  var SIDES = ['drive', 'rotor'];
  var TIMES = '×';
  var DASH_LENGTH = '-';

  /* ------------------------------------------------------------------ bridge helpers */
  function B() { return global.__loamBuilderInternals || null; }
  function bs() { var b = B(); return b ? b.buildState : null; }
  function fn(name) { var b = B(); return b && b.fns && typeof b.fns[name] === 'function' ? b.fns[name] : function () {}; }
  function caches() { var b = B(); return b ? b.caches : { rims: [], hubs: [], spokes: [] }; }
  function isStandard() { var s = bs(); return !!(s && s.spokeInterface !== 'system'); }
  function cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function warn(msg) { if (global.console) global.console.warn('[STANDARD-STUDIO] ' + msg); }
  function money(cents) { return fn('formatPrice')(cents); }
  function positions() {
    var s = bs(); if (!s || !s.buildType) return [];
    var out = []; if (s.buildType === 'Front' || s.buildType === 'Wheel Set') out.push('front'); if (s.buildType === 'Rear' || s.buildType === 'Wheel Set') out.push('rear'); return out;
  }
  function optionOf(variant, name) { var o = variant && variant.selectedOptions ? variant.selectedOptions.find(function (x) { return x.name && x.name.toLowerCase() === name; }) : null; return o ? o.value : null; }
  function colorOf(variant) { return optionOf(variant, 'color'); }
  function lengthOf(variant) { return optionOf(variant, 'length'); }
  function hexFor(name) { return CFG.colorHex[name] || null; }
  function colorEntry(name) { var h = hexFor(name); return h ? { name: name, hex: h } : { name: name }; }
  function filled(n, v) { var a = []; for (var i = 0; i < n; i++) a.push(v); return a; }
  function spokeCount(pos) { var s = bs(); var raw = s && s.specs ? s.specs[pos + 'SpokeCount'] : null; var n = parseInt(String(raw || '').replace(/\D/g, ''), 10); return isFinite(n) && n > 0 ? n : 0; }

  /* ------------------------------------------------------------------ state: buildState.standardStudio = { v, studioOpened, studio, wheels } */
  function st() { var s = bs(); return s && s.standardStudio ? s.standardStudio : null; }
  function ensureSt() { var s = bs(); if (!s) return null; if (!s.standardStudio || typeof s.standardStudio !== 'object') s.standardStudio = { v: 1, studioOpened: false, studio: null, wheels: {} }; if (!s.standardStudio.wheels) s.standardStudio.wheels = {}; return s.standardStudio; }
  function paint(pos) { var v = st() && st().studio; var sp = v && v.spokes && v.spokes[pos]; return sp && Array.isArray(sp.drive) && Array.isArray(sp.rotor) ? sp : null; }
  function clearPainting(pos) {
    var v = st() && st().studio; if (!v) return;
    if (v.spokes) delete v.spokes[pos];
    if (v.base) delete v.base[pos];
    if (!v.spokes || !Object.keys(v.spokes).length) st().studio = null;
    if (st() && st().wheels) delete st().wheels[pos];
  }

  /* ------------------------------------------------------------------ spoke product, usable colors, paint flags */
  function spokesComp(pos) { var s = bs(); var c = s && s.components ? s.components[pos + 'Spokes'] : null; return c && !c.isVirtual && !c.isIncluded && !c.i9 ? c : null; }
  function productOf(comp) { return comp && comp.productId ? caches().spokes.find(function (p) { return p.id === comp.productId; }) || null : null; }
  function usableVariants(product) {
    if (!product) return [];
    return (product.variants || []).filter(function (v) {
      var c = colorOf(v), len = lengthOf(v);
      return v.availableForSale && c && CFG.ignoreColors.indexOf(c) < 0 && (len === DASH_LENGTH || len === null);
    });
  }
  function colorNames(product) { var seen = {}, out = []; usableVariants(product).forEach(function (v) { var c = colorOf(v); if (!seen[c]) { seen[c] = 1; out.push(c); } }); return out; }
  function variantFor(product, color) { return usableVariants(product).find(function (v) { return colorOf(v) === color; }) || null; }
  // Colors that must be painted: ANY variant of the product with that Color has custom.paint_fee_color = Yes (empty counts as no).
  function paintNamesOf(product) {
    var seen = {}, out = [];
    ((product && product.variants) || []).forEach(function (v) {
      var flag = String(v.paintFeeColor == null ? '' : v.paintFeeColor).trim().toLowerCase();
      var c = colorOf(v);
      if (flag === 'yes' && c && CFG.ignoreColors.indexOf(c) < 0 && !seen[c]) { seen[c] = 1; out.push(c); }
    });
    return out;
  }
  function dataReady(pos) { var c = spokesComp(pos); return !!(c && caches().spokes.length && productOf(c)); }
  // The Studio is offered on a wheel whose Standard spoke product has 2 or more usable colors and whose spoke count is known.
  function offered(pos) {
    if (!isStandard() || positions().indexOf(pos) < 0) return false;
    var c = spokesComp(pos); if (!c || !spokeCount(pos)) return false;
    var p = productOf(c); return !!p && colorNames(p).length >= CFG.minColors;
  }

  /* ------------------------------------------------------------------ the fee rule (ONE function, used by the Studio, the cart and the tests) */
  // paintNames: the product's painted colors ([] for a steel-type product); used: the colors on the wheel. Returns 'mixed' | 'painted' | 'multi' | null.
  function feeTypeFor(paintNames, used) {
    var colors = (used || []).filter(function (c, i, a) { return c && a.indexOf(c) === i; });
    var pn = paintNames || [];
    if (!pn.length) return colors.length > 1 ? 'mixed' : null;                        // steel-type: more than one color = Mixed colors
    var painted = colors.filter(function (c) { return pn.indexOf(c) >= 0; });
    if (painted.length >= 2) return 'multi';
    if (painted.length === 1) return 'painted';
    return colors.length > 1 ? 'mixed' : null;                                        // only Black Berd and/or White Berd: both = Mixed, one = none
  }
  var feeTable = null; // fee type -> { gid, cents, label } from the store (option value lookup); null until loaded
  function feeEntry(type) {
    var base = CFG.fee.types[type]; if (!base) return null;
    var f = feeTable && feeTable[type];
    return { type: type, gid: f ? f.gid : base.gid, cents: f ? f.cents : base.cents, label: base.value };
  }
  function countsToColors(counts) { return Object.keys(counts || {}).filter(function (k) { return counts[k] > 0; }); }

  /* ------------------------------------------------------------------ counts and labels */
  function counts(pos) {
    var comp = spokesComp(pos); if (!comp) return {};
    var sp = paint(pos), out = {};
    if (sp) { SIDES.forEach(function (side) { sp[side].forEach(function (c) { out[c] = (out[c] || 0) + 1; }); }); return out; }
    if (comp.selectedColor) out[comp.selectedColor] = spokeCount(pos) || 0;
    return out;
  }
  function usedColors(pos) { var c = counts(pos), names = countsToColors(c); if (!names.length && spokesComp(pos) && spokesComp(pos).selectedColor) names = [spokesComp(pos).selectedColor]; return names; }
  // The Studio's base color for a wheel (the list color shown in Step 3); without painting it is the list color itself.
  function baseColorOf(pos) { var v = st() && st().studio, b = v && v.base && v.base[pos]; return b && b.drive ? b.drive : (spokesComp(pos) ? spokesComp(pos).selectedColor : null); }
  // Most-used color first; equal counts go to the Studio's base color, then by name (so the answer never depends on spoke order).
  function sortedColors(pos) {
    var c = counts(pos), base = baseColorOf(pos);
    return countsToColors(c).sort(function (a, b) { if (c[b] !== c[a]) return c[b] - c[a]; if (a === base) return -1; if (b === base) return 1; return a < b ? -1 : 1; });
  }
  function isCustom(pos) { return countsToColors(counts(pos)).length > 1; }
  function lineColor(pos) { var n = sortedColors(pos); return n.length ? n[0] : null; }
  // The cart line of a custom-colored Standard wheel is the variant of its MOST-USED color (not the base color kept in the list). Returns that
  // variant, or null when the list variant is already the right one. A color whose price differs from the list variant's never replaces it:
  // the builder's total is computed from the list variant, so the line must cost the same.
  function lineVariant(pos) {
    var comp = spokesComp(pos); if (!comp || !isStandard() || !isCustom(pos)) return null;
    var p = productOf(comp), color = lineColor(pos); if (!p || !color || color === comp.selectedColor) return null;
    var v = variantFor(p, color); if (!v) return null;
    if (v.price && comp.price != null && v.price.amount !== comp.price) { warn('cart line keeps the list color: ' + color + ' is priced differently'); return null; }
    return v;
  }
  function lineVariantGid(position, comp) { var v = lineVariant(String(position).toLowerCase()); return v ? v.id : (comp ? (comp.variantId || comp.variantGid) : null); }
  // Stock of the line's variant, for the lead time; undefined = use the component's own.
  function lineQuantityAvailable(key) {
    var m = /^(front|rear)Spokes$/.exec(String(key)); if (!m) return undefined;
    var v = lineVariant(m[1]); return v && v.quantityAvailable !== undefined ? v.quantityAvailable : undefined;
  }
  // "Oriole Orange, plus 2 more colors": the most-used color, then how many others.
  function colorLabel(pos) {
    var names = sortedColors(pos); if (!names.length) return '';
    if (names.length === 1) return names[0];
    var more = names.length - 1; return names[0] + ', plus ' + more + ' more color' + (more === 1 ? '' : 's');
  }
  function mixText(pos) { var c = counts(pos); return sortedColors(pos).map(function (n) { return n + ' ' + TIMES + c[n]; }).join(', '); }

  /* ------------------------------------------------------------------ lacing and Studio config */
  function hubComp(pos) { var s = bs(); var c = s && s.components ? s.components[pos + 'Hub'] : null; return c && !c.isVirtual ? c : null; }
  function hubProduct(pos) { var c = hubComp(pos); return c && c.productId ? caches().hubs.find(function (p) { return p.id === c.productId; }) || null : null; }
  function hubColorName(pos) { var c = hubComp(pos); return c ? optionOf(c, 'color') : null; }
  // True when the builder's estimate came from its last rule, "the largest crossing that is geometrically possible" (no hub lacing policy
  // applies, no manual cross value, not a radial rim-brake front). Mirrors the order of getEstimatedCrossPattern's checks.
  function crossFromLargestRule(hp, variant, total, position, brake) {
    if (!hp || !total) return false; // the builder's own fixed fallback (3): nothing to cap
    var policy = hp.hub_lacing_policy && hp.hub_lacing_policy.value;
    if (policy === 'Use Manual Override Field' && variant && variant.hub_manual_cross_value && variant.hub_manual_cross_value.value) return false;
    if (policy === 'Force 2-Cross for 28h Only' && total === 28) return false;
    if (policy === 'Force 3-Cross for 28h Only' && total === 28) return false;
    if (policy === 'Force All as 2-Cross') return false;
    if (position === 'Front' && brake === 'Rim Brake' && total <= 24) return false;
    return true;
  }
  // The builder's estimate gives 4-cross from 36 holes, but the spoke calculator the orders are built from uses 3-cross by default from 28 holes up.
  // So the Studio caps the estimate at 3 when it comes from that "largest possible" rule on a wheel with 28 or more holes. Policies, manual values and
  // anything under 28 holes are left as the builder gives them; getEstimatedCrossPattern itself is not changed.
  function crossFor(pos) {
    var s = bs(), hp = hubProduct(pos), hc = hubComp(pos), total = spokeCount(pos), brake = s.specs ? s.specs.brakeStyle : null;
    var variant = hp && hc ? (hp.variants || []).find(function (v) { return v.id === hc.variantId; }) : null;
    var cross = fn('getEstimatedCrossPattern')(hp, variant, total, cap(pos), brake);
    if (cross > 3 && total >= CFG.crossCapFrom && crossFromLargestRule(hp, variant, total, cap(pos), brake)) cross = 3;
    return cross;
  }
  function positive(v) { var n = parseFloat(v); return isFinite(n) && n > 0 ? n : null; }
  function lacing(pos) {
    var total = spokeCount(pos), hp = hubProduct(pos);
    var cross = crossFor(pos);
    // right = drive side for both wheels (rear drive side, front right side); left = the other side
    return {
      drive: { n: Math.ceil(total / 2), pcd: positive(hp && hp.hub_flange_diameter_right) || CFG.defaultPcd, cross: cross },
      rotor: { n: Math.floor(total / 2), pcd: positive(hp && hp.hub_flange_diameter_left) || CFG.defaultPcd, cross: cross }
    };
  }
  function lacingKnown(pos) { return !!hubProduct(pos) || !hubComp(pos); }
  // Photo ring (config.rimImage): Industry Nine rims get the I9 ring photo, every other brand (We Are One and the rest) the We Are One ring; no rim yet = We Are One.
  function rimImageFor(pos, rim) {
    try { return global.LoamStudio && typeof global.LoamStudio.rimImage === 'function' ? global.LoamStudio.rimImage(rim && rim.vendor, { position: pos }) : null; } catch (e) { return null; }
  }
  // Valve stem color per wheel (Studio config.valveColor): set by setValveColor, nothing sets it yet (the Studio draws its dark default).
  var valveColors = {};
  function setValveColor(a, b) {
    var m = {}; if (a && typeof a === 'object') m = a; else if (a === 'front' || a === 'rear') m[a] = b; else if (typeof a === 'string') m = { front: a, rear: a };
    ['front', 'rear'].forEach(function (k) { if (k in m) { if (m[k]) valveColors[k] = String(m[k]); else delete valveColors[k]; } });
    var s = bs(); if (!s || !isStandard()) return;
    positions().forEach(function (pos) { var c = s.components && s.components[pos + 'Spokes']; if (c && document.querySelector('#' + pos + '-spokes-subsection .ss-card')) fn('updateComponentUI')(cap(pos), 'Spokes', c); }); // redraws each card's wheel
  }
  function wheelConfig(pos) {
    var s = bs(), l = lacing(pos), rim = s.components[pos + 'Rim'], hc = hubComp(pos), total = spokeCount(pos);
    var wc = {
      hubName: hc ? (hc.title || '') : '',
      hubColor: hubColorName(pos) || CFG.neutralHub.name,
      erd: positive(rim && rim.rim_erd) || CFG.defaultErd,
      rimHoles: total,
      drive: l.drive, rotor: l.rotor,
      sideLabels: CFG.sideLabels[pos]
    };
    var ri = rimImageFor(pos, rim); if (ri) wc.rimImage = ri; // a Studio without photo rims ignores the key
    if (valveColors[pos]) wc.valveColor = valveColors[pos];
    return wc;
  }
  function hubColorList(pos) { var n = hubColorName(pos); return [n ? colorEntry(n) : { name: CFG.neutralHub.name, hex: CFG.neutralHub.hex }]; }
  // config.feeInfo for the wheel's spoke product: a steel-type product (no painted colors) has a no-fee row and one fee row, a Berd-type product a no-fee row and three fee rows.
  function feeInfoFor(pos) {
    var p = productOf(spokesComp(pos)), berd = paintNamesOf(p).length > 0, info = CFG.feeInfo;
    var rows = (berd ? info.painted : info.steel).map(function (r) {
      var type = feeTypeFor(berd ? ['P1', 'P2'] : [], r.example), f = type ? feeEntry(type) : null;
      return { label: r.label, amount: f ? f.cents / 100 : 0, when: r.when };
    });
    return { title: info.title, rows: rows, note: info.note, idle: info.idle };
  }
  function accentFor(names, base) { return names.find(function (n) { return n !== base; }) || names[0]; }
  function feeForInfo(info) {
    var comp = spokesComp(info.wheel), p = productOf(comp); if (!p) return { amount: 0 };
    var f = feeEntry(feeTypeFor(paintNamesOf(p), countsToColors(info.counts)));
    return f ? { amount: f.cents / 100, label: f.label } : { amount: 0 };
  }
  // Both wheels open in one Studio (Front / Rear / Both tabs, one shared palette) only when both are in the build, both are offered and both
  // use the SAME spoke product. Any other case (one wheel in the build, two different products) is a single-wheel Studio.
  function sharedProduct() {
    var s = bs(); if (!s || !isStandard() || s.buildType !== 'Wheel Set') return false;
    if (!offered('front') || !offered('rear')) return false;
    return spokesComp('front').productId === spokesComp('rear').productId;
  }
  function studioPositions(pos) { return sharedProduct() ? ['front', 'rear'] : [pos]; }
  // poss: the wheels in this Studio; pos: the wheel whose card was used (its tab opens first, and its list color is the default base).
  function studioConfig(poss, pos) {
    var comp = spokesComp(pos), p = productOf(comp), names = colorNames(p), base = comp.selectedColor;
    var wheels = {}, hubs = {}; poss.forEach(function (w) { wheels[w] = wheelConfig(w); hubs[w] = hubColorList(w); });
    var arrows = global.I9System && global.I9System.CFG ? global.I9System.CFG.arrowImages : null;
    var cfg = {
      title: (comp.title || 'Spokes') + ' · ' + (poss.length === 2 ? 'front and rear wheels' : pos + ' wheel'),
      fee: { currency: 'USD' },
      feeFor: feeForInfo,
      feeInfo: feeInfoFor(pos), // the fee explanation popup (a Studio without it ignores the key)
      spokeColors: names.map(colorEntry),
      hubColors: hubs,
      wheels: wheels,
      defaultBase: names.indexOf(base) >= 0 ? base : names[0],
      defaultAccent: accentFor(names, base),
      startWheel: pos,
      autoValvePhase: true, // Agent A's Studio option: valve between the most parallel spokes. A Studio without it ignores the key. Never set for System.
      onFeedbackRequest: requestFeedback
    };
    if (arrows) cfg.arrowImages = arrows;
    return cfg;
  }
  function requestFeedback() { var link = document.querySelector('.lls-overlay [data-act="fbopen"]'); if (global.LoamFeedback) global.LoamFeedback.open(link); else warn('feedback link module not loaded'); }

  /* ------------------------------------------------------------------ keeping painting valid */
  var notices = {};
  // The notices belong to one build: when another build is loaded in this page (Resume, Saved Builds, a shared link, edit from cart) the previous build's notices are dropped.
  var noticeOwner = null;
  function syncNoticeOwner() { var s = bs(), id = s && s.buildId ? s.buildId : null; if (id !== noticeOwner) { notices = {}; noticeOwner = id; } }
  function showNotice(pos, text) { syncNoticeOwner(); notices[pos] = text || ''; var el = document.querySelector('#' + pos + '-spokes-subsection .ss-card-notice'); if (el) { el.textContent = notices[pos]; el.hidden = !notices[pos]; } }
  function sameLacing(a, b) { return !!(a && b && a.drive && b.drive && a.rotor && b.rotor && a.drive.n === b.drive.n && a.rotor.n === b.rotor.n && a.drive.cross === b.drive.cross && a.rotor.cross === b.rotor.cross); }
  // Repairs one wheel's painting against the builder's current state. A list pick, a Match, an auto-pair or a product change all arrive here as
  // "the list color is no longer the Studio's base color": the wheel becomes all spokes that color. A changed spoke count or lacing resets it with a notice.
  function reconcile(pos) {
    var s = bs(); if (!s || !isStandard()) return;
    var sp = paint(pos); if (!sp) return;
    var comp = spokesComp(pos);
    if (!comp || !caches().spokes.length) { if (!comp) clearPainting(pos); return; }   // data not loaded yet: leave it, no false reset on load
    var p = productOf(comp);
    if (!p || !offered(pos)) { clearPainting(pos); return; }
    var v = st().studio, names = colorNames(p);
    var base = v.base && v.base[pos] ? v.base[pos].drive : null;
    if (base !== comp.selectedColor) { clearPainting(pos); return; }
    var l = lacing(pos);
    var arraysOk = sp.drive.length === l.drive.n && sp.rotor.length === l.rotor.n;
    var lacingChanged = lacingKnown(pos) && st().wheels[pos] && !sameLacing(st().wheels[pos], l);
    if (!arraysOk || lacingChanged) { clearPainting(pos); showNotice(pos, CFG.text.resetNotice.replace('{wheel}', pos)); return; }
    var replaced = 0;
    SIDES.forEach(function (side) {
      var sideBase = v.base[pos][side];
      if (names.indexOf(sideBase) < 0) { v.base[pos][side] = base; sideBase = base; replaced++; }
      sp[side] = sp[side].map(function (c) { if (names.indexOf(c) >= 0) return c; replaced++; return sideBase; });
    });
    if (replaced) showNotice(pos, CFG.text.replacedNotice);
    if (lacingKnown(pos)) st().wheels[pos] = { drive: l.drive, rotor: l.rotor };
    var hc = hubColorName(pos); if (hc) { v.hubColor = v.hubColor || {}; v.hubColor[pos] = hc; }
  }

  /* ------------------------------------------------------------------ fee lines */
  function syncFee(pos) {
    var s = bs(); if (!s || !s.components) return;
    var key = pos + 'RequiredAddons';
    var arr = Array.isArray(s.components[key]) ? s.components[key] : null;
    if (arr) s.components[key] = arr.filter(function (a) { return !(a && a.ssFee); }); // never two fee lines on a wheel
    if (!isStandard() || positions().indexOf(pos) < 0 || !dataReady(pos)) return;
    var p = productOf(spokesComp(pos)), type = feeTypeFor(paintNamesOf(p), usedColors(pos)), f = type ? feeEntry(type) : null;
    if (!f) return;
    if (!Array.isArray(s.components[key])) s.components[key] = [];
    s.components[key].push({ name: CFG.fee.name + ' (' + f.label + ')', price: f.cents / 100, variantGid: f.gid, costInCents: f.cents, ssFee: true, feeType: type });
  }
  function applyColorFee(position) { syncFee(String(position).toLowerCase()); }
  // The store's 10% code does not apply to the fee product, so the builder's estimate leaves it out too (same as the System fee).
  function isDiscountExempt(addon) { return !!(addon && addon.ssFee === true); }
  function feeAddons() {
    var s = bs(), out = []; if (!s) return out;
    positions().forEach(function (pos) { (s.components[pos + 'RequiredAddons'] || []).forEach(function (a) { if (a && a.ssFee) out.push({ pos: pos, addon: a }); }); });
    return out;
  }
  function feeCents() { return feeAddons().reduce(function (sum, x) { return sum + (x.addon.costInCents || 0); }, 0); }
  // A wheel with no spoke product selected has no Studio card and no custom painting. Whatever cleared the selection (a changed riding style,
  // spoke count, rim, hub or vendor, a build type change, Reset) leaves the state without that wheel's spokes, so this runs from refresh(),
  // from a cleared empty slot (decorateComponentUI) and when Step 3's UI is cleared. The card comes back when decorateComponentUI draws a chosen product.
  function syncCards() {
    var s = bs(); if (!s) return;
    ['front', 'rear'].forEach(function (pos) {
      var has = !!spokesComp(pos);
      if (!has) { clearPainting(pos); notices[pos] = ''; }
      if (!has || !isStandard()) { var card = document.querySelector('#' + pos + '-spokes-subsection .ss-card'); if (card) card.remove(); }
    });
    if (s.standardStudio && !spokesComp('front') && !spokesComp('rear')) s.standardStudio = null; // nothing left to paint: the Studio state starts over
  }
  // Runs before every subtotal / summary / cart build: repair painting, then refresh each wheel's fee line (one per wheel, follows colors, counts, build type).
  function refresh() {
    var s = bs(); if (!s) return;
    syncCards();
    if (!isStandard()) return;
    positions().forEach(reconcile);
    ['front', 'rear'].forEach(syncFee); // also drops the fee line of a wheel that left the build (syncFee removes, and only adds for a wheel in the build)
  }

  /* ------------------------------------------------------------------ choosing colors */
  function componentForVariant(product, variant, prev) {
    var color = colorOf(variant);
    return {
      variantId: variant.id, productId: product.id, productHandle: product.handle, title: product.title, variantTitle: variant.title, sku: variant.sku,
      price: variant.price.amount, vendor: product.vendor, image: variant.image, pairingKey: product.pairingKey, selectedOptions: variant.selectedOptions,
      optionalAddonProductGids: product.optionalAddonProductGidsFromProduct || [], quantityAvailable: variant.quantityAvailable, leadTimeDays: product.leadTimeDays || 0,
      wheelModel: product.wheelModel, selectedColor: color, weight_g: variant.weight_g != null ? variant.weight_g : (product.weight_g != null ? product.weight_g : null)
    };
  }
  // The list's selected color follows the Studio's base color: the same product's variant for that color replaces the spoke component.
  function setListColor(pos, color) {
    var s = bs(), comp = spokesComp(pos), p = productOf(comp); if (!comp || !p) return false;
    if (comp.selectedColor === color) return true;
    var v = variantFor(p, color); if (!v) { warn('no spoke variant for color ' + color); return false; }
    var next = componentForVariant(p, v, comp);
    fn('manageStickyAddonsForChangedBaseVariant')(cap(pos), 'Spokes', comp.variantId, next.variantId, next.optionalAddonProductGids);
    s.components[pos + 'Spokes'] = next; s._ui.hasChanges = true; fn('updateStep3ButtonText')();
    return true;
  }
  // A color picked in the spoke list means "all spokes this color": painting for that wheel is cleared (called after the list click wrote the component).
  function onListPick(position) { var pos = String(position).toLowerCase(); if (!isStandard()) return; clearPainting(pos); showNotice(pos, ''); }
  // Match to Rear/Front and auto-pairing write the other wheel's spoke component: that wheel becomes all spokes that color.
  function onSpokesAssigned(position) { onListPick(position); }

  /* ------------------------------------------------------------------ the Studio */
  function freshValue(pos, cfg, base) {
    var l = cfg.wheels[pos], hubColor = cfg.wheels[pos].hubColor;
    var v = { v: 1, hubColor: {}, base: {}, spokes: {}, accent: cfg.defaultAccent };
    v.hubColor[pos] = hubColor; v.base[pos] = { drive: base, rotor: base };
    v.spokes[pos] = { drive: filled(l.drive.n, base), rotor: filled(l.rotor.n, base) };
    return v;
  }
  function studioValueForOne(pos, cfg) {
    var comp = spokesComp(pos), base = comp.selectedColor, sp = paint(pos), whole = st() && st().studio;
    if (sp && whole && whole.base && whole.base[pos]) {
      var v = { v: 1, hubColor: {}, base: {}, spokes: {}, accent: whole.accent || cfg.defaultAccent };
      v.hubColor[pos] = cfg.wheels[pos].hubColor; v.base[pos] = JSON.parse(JSON.stringify(whole.base[pos])); v.spokes[pos] = JSON.parse(JSON.stringify(sp));
      return v;
    }
    return freshValue(pos, cfg, base);
  }
  // The value the Studio starts from: each wheel in `poss` as painted (or a solid wheel in its list color), one shared accent.
  function studioValueFor(poss, cfg) {
    var out = null;
    poss.forEach(function (pos) {
      var one = studioValueForOne(pos, cfg);
      if (!out) { out = one; return; }
      out.hubColor[pos] = one.hubColor[pos]; out.base[pos] = one.base[pos]; out.spokes[pos] = one.spokes[pos];
    });
    return out;
  }
  function mergeValue(pos, value) {
    var sst = ensureSt(); var whole = sst.studio && sst.studio.v === 1 ? sst.studio : { v: 1, hubColor: {}, base: {}, spokes: {}, accent: value.accent };
    whole.hubColor = whole.hubColor || {}; whole.base = whole.base || {}; whole.spokes = whole.spokes || {};
    if (value.hubColor && value.hubColor[pos]) whole.hubColor[pos] = value.hubColor[pos];
    whole.base[pos] = JSON.parse(JSON.stringify(value.base[pos]));
    whole.spokes[pos] = JSON.parse(JSON.stringify(value.spokes[pos]));
    if (value.accent) whole.accent = value.accent;
    sst.studio = whole;
  }
  var studioFrom = null;
  // Takes one wheel's part of the Studio value into the builder state (no UI refresh yet).
  function applyOne(pos, value) {
    var sp = value.spokes[pos], distinct = {};
    SIDES.forEach(function (side) { sp[side].forEach(function (c) { distinct[c] = 1; }); });
    var names = Object.keys(distinct);
    if (names.length === 1) {
      // one color everywhere is a solid wheel: the list color becomes that color and no painting is kept
      clearPainting(pos); setListColor(pos, names[0]);
    } else {
      mergeValue(pos, value);
      var base = value.base[pos].drive; setListColor(pos, base);
      var l = lacing(pos); ensureSt().wheels[pos] = { drive: l.drive, rotor: l.rotor };
    }
  }
  // poss: the wheels that were in this Studio. All wheels are written first and refreshed afterwards, so a refresh never sees one wheel half-applied.
  function applyStudioValue(poss, value, closing) {
    var s = bs(); if (!s || !value || !value.spokes) return;
    var done = poss.filter(function (pos) { return value.spokes[pos] && spokesComp(pos); });
    if (!done.length) return;
    done.forEach(function (pos) { applyOne(pos, value); });
    if (closing) ensureSt().studioOpened = true;
    done.forEach(afterChange);
    if (closing) setTimeout(function () { var b = document.querySelector('#' + (studioFrom || done[0]) + '-spokes-subsection button[data-ss-action="studio"]'); if (b) b.focus({ preventScroll: true }); }, 0);
  }
  function afterChange(pos) {
    var s = bs(); if (!s) return;
    fn('calculateWheelCosts')(cap(pos));
    fn('updateComponentUI')(cap(pos), 'Spokes', s.components[pos + 'Spokes']);
    fn('updateHubSpokeCompatibilityUI')();
    fn('updateSubtotal')();
    fn('validateStep3')();
    fn('handleComponentChangeForLeadTime')();
    fn('saveLastActiveBuild')();
  }
  function openStudio(position) {
    var pos = String(position).toLowerCase(), s = bs();
    if (!s || !global.LoamStudio) { warn('Studio unavailable'); return; }
    if (!offered(pos)) return;
    var poss = studioPositions(pos);
    poss.forEach(reconcile);
    var cfg = studioConfig(poss, pos);
    var value = studioValueFor(poss, cfg);
    studioFrom = pos; poss.forEach(function (w) { showNotice(w, ''); });
    var sst = ensureSt(); sst.studioOpened = true;
    global.LoamStudio.open({
      config: cfg, value: value,
      onChange: function (v) { applyStudioValue(poss, v, false); },
      onClose: function (v) { applyStudioValue(poss, v, true); }
    });
  }

  /* ------------------------------------------------------------------ Step 3 card, selected line */
  // The wheel thumbnail is System's own: the same dark stage (.i9-stage) and LoamStudio.preview call as the System Step 3 card, at the same size.
  function previewSize() { return (global.innerWidth && global.innerWidth < 768) ? 240 : ((global.I9System && global.I9System.CFG && global.I9System.CFG.previewSize) || 240); }
  function stageHtml(pos) {
    return '<div class="i9-stage ss-stage" data-ss-action="studio" data-position="' + pos + '"><div class="i9-stage-top"><span>Left side view</span></div><div class="i9-preview-wheel"></div></div>';
  }
  // Draws the wheel in its current colors (a solid wheel in the list color until the customer paints). A failure leaves the card without a thumbnail.
  var lastPreviewSize = null;
  function mountPreview(card, pos) {
    var slot = card.querySelector('.i9-preview-wheel'); if (!slot) return;
    if (!global.LoamStudio || typeof global.LoamStudio.preview !== 'function') { var st0 = card.querySelector('.ss-stage'); if (st0) st0.remove(); return; }
    try {
      var cfg = studioConfig([pos], pos);
      lastPreviewSize = previewSize();
      global.LoamStudio.preview(slot, { config: cfg, value: studioValueFor([pos], cfg), wheel: pos, size: lastPreviewSize, pov: 'rotor', dark: true });
    } catch (e) { warn('preview failed for ' + pos + ': ' + e.message); var st1 = card.querySelector('.ss-stage'); if (st1) st1.remove(); }
  }
  function cardHtml(pos) {
    syncNoticeOwner();
    var custom = isCustom(pos);
    return '<div class="ss-card-title">' + esc(CFG.text.cardTitle) + '</div>' + stageHtml(pos) +
      (custom ? '<div class="ss-card-label">' + esc(colorLabel(pos)) + '</div>' : '') +
      '<button type="button" class="ss-card-button" data-ss-action="studio" data-position="' + pos + '">' + esc(CFG.text.cardButton) + '</button>' +
      '<div class="ss-card-notice" role="status"' + (notices[pos] ? '' : ' hidden') + '>' + esc(notices[pos] || '') + '</div>';
  }
  // Called after every updateComponentUI (also for an empty slot): draws, updates or removes the card and the custom-color wording of the selected line.
  function decorateComponentUI(position, typePascal, componentData) {
    if (typePascal !== 'Spokes') return;
    var pos = String(position).toLowerCase();
    var sub = document.getElementById(pos + '-spokes-subsection'); if (!sub) return;
    var card = sub.querySelector('.ss-card');
    if (!componentData) syncCards(); // an emptied slot: no card, no leftover painting
    if (!isStandard() || !componentData || !offered(pos)) { if (card) card.remove(); return; }
    refresh();
    if (!card) {
      card = document.createElement('div'); card.className = 'ss-card'; card.dataset.position = pos;
      var sel = document.getElementById(pos + '-spokes-selection');
      if (sel) sel.insertAdjacentElement('afterend', card); else sub.appendChild(card);
      card.addEventListener('click', function (e) { var b = e.target.closest('[data-ss-action="studio"]'); if (b) { e.preventDefault(); openStudio(pos); } });
    }
    card.innerHTML = cardHtml(pos);
    mountPreview(card, pos);
    var span = document.querySelector('#' + pos + '-spokes-selection span');
    var strong = span ? span.querySelector('strong') : null;
    if (strong && isCustom(pos)) strong.textContent = (componentData.title || '') + ' (' + colorLabel(pos) + ')';
  }

  /* ------------------------------------------------------------------ summary, cart, order properties, _build */
  function splitSpokes(type) { if (type !== 'Spokes' || !isStandard()) return false; return positions().some(function (p) { return !!spokesComp(p) && isCustom(p); }); }
  function summaryDescription(type, component, pos) {
    if (type !== 'Spokes' || !isStandard() || !component || component.i9 || !spokesComp(pos) || !isCustom(pos)) return null;
    return (component.title || '') + ' (' + colorLabel(pos) + ')';
  }
  function summaryFeeRows() {
    if (!isStandard()) return '';
    var html = '';
    feeAddons().forEach(function (x) { html += '<li><span class="summary-item-label-column">' + cap(x.pos) + ':</span><span class="summary-item-description-column"><strong>(1) ' + esc(x.addon.name) + '</strong></span> <span class="summary-value">(' + money(x.addon.costInCents) + ')</span></li>'; });
    return html;
  }
  // For a pre-configured wheel (one master variant): the fee lines are added next to it so painted or mixed spokes are never free.
  function feeCartLines(baseProps) {
    var toId = fn('getNumericIdFromShopifyGid'), out = [];
    feeAddons().forEach(function (x) { if (x.addon.variantGid) out.push({ id: toId(x.addon.variantGid), quantity: 1, properties: Object.assign({}, baseProps, { Wheel: cap(x.pos) }) }); });
    return out;
  }
  // Only a wheel with custom painting gets readable properties, so a solid (or never-opened) Standard wheel's cart lines are unchanged.
  function readableProperties() {
    var s = bs(), props = {}; if (!s || !isStandard()) return props;
    positions().forEach(function (pos) {
      if (!spokesComp(pos) || !isCustom(pos)) return;
      var P = cap(pos), sp = paint(pos);
      props[P + ' Spoke Colors'] = mixText(pos);
      if (sp) SIDES.forEach(function (side) { props[P + ' ' + cap(side) + ' Side'] = (sp[side] || []).map(function (c, i) { return (i + 1) + ' ' + c; }).join(', '); });
    });
    return props;
  }
  // The contract the backend reads (_build.standardStudio). null when no wheel has a Standard spoke product (the key is then left out).
  function snapshot() {
    var s = bs(); if (!s || !isStandard() || !s.components) return null;
    var act = positions().filter(function (pos) { return !!spokesComp(pos); });
    if (!act.length) return null;
    var sst = st() || { studioOpened: false, studio: null };
    var out = { v: 1, studioOpened: !!sst.studioOpened, studio: sst.studio ? JSON.parse(JSON.stringify(sst.studio)) : null, wheels: {}, colors: {}, paint: { blank: CFG.paint.blank, wheels: {} } };
    var addColor = function (name) { if (name) out.colors[name] = hexFor(name); };
    act.forEach(function (pos) {
      var p = productOf(spokesComp(pos)), l = lacing(pos);
      out.wheels[pos] = { drive: l.drive, rotor: l.rotor, erd: wheelConfig(pos).erd, sideLabels: { drive: CFG.sideLabels[pos].drive, rotor: CFG.sideLabels[pos].rotor } };
      out.paint.wheels[pos] = { paintColors: paintNamesOf(p) };
      Object.keys(counts(pos)).forEach(addColor); addColor(spokesComp(pos).selectedColor); addColor(hubColorName(pos));
    });
    if (out.studio) { var hub = out.studio.hubColor || {}; Object.keys(hub).forEach(function (k) { if (act.indexOf(k) >= 0) addColor(hub[k]); }); }
    return out;
  }
  // Called by the load path (edit from cart, Resume, Saved Builds, share link) with the saved snapshot.
  function restore(saved) {
    var s = bs(); if (!s) return;
    if (!saved || typeof saved !== 'object') { s.standardStudio = null; return; }
    s.standardStudio = { v: 1, studioOpened: !!saved.studioOpened, studio: saved.studio ? JSON.parse(JSON.stringify(saved.studio)) : null, wheels: {} };
    if (saved.wheels) Object.keys(saved.wheels).forEach(function (pos) { var w = saved.wheels[pos]; if (w && w.drive && w.rotor) s.standardStudio.wheels[pos] = { drive: w.drive, rotor: w.rotor }; });
  }
  // Called after Step 3 has rendered (and caches are in): repair painting, redraw the cards and refresh fees.
  function afterStep3Render() {
    var s = bs(); if (!s || !isStandard()) return;
    refresh();
    positions().forEach(function (pos) { var c = s.components[pos + 'Spokes']; if (c) fn('updateComponentUI')(cap(pos), 'Spokes', c); });
  }

  /* ------------------------------------------------------------------ fee prices from the store (by option VALUE) */
  async function loadFeePrices() {
    try {
      var q = 'query($id: ID!) { node(id: $id) { ... on ProductVariant { product { variants(first: 50) { edges { node { id availableForSale price { amount } selectedOptions { name value } } } } } } } }';
      var data = await fn('storefrontApiQuery')(q, { id: CFG.fee.types.mixed.gid });
      var edges = data && data.node && data.node.product && data.node.product.variants ? data.node.product.variants.edges : null;
      if (!edges) { warn('fee product not readable through the Storefront API; using the built-in fee table'); return; }
      var table = {};
      Object.keys(CFG.fee.types).forEach(function (type) {
        var want = CFG.fee.types[type].value;
        var hit = edges.map(function (e) { return e.node; }).find(function (n) { return (n.selectedOptions || []).some(function (o) { return o.name === CFG.fee.optionName && o.value === want; }); });
        if (hit && isFinite(parseFloat(hit.price.amount))) { table[type] = { gid: hit.id, cents: Math.round(parseFloat(hit.price.amount) * 100) }; if (!hit.availableForSale) warn('fee variant "' + want + '" is not available for sale'); }
        else warn('fee variant "' + want + '" not found by option value; using the built-in gid');
      });
      feeTable = table;
      var s = bs(); if (s && s.step >= 3) { positions().forEach(function (pos) { fn('calculateWheelCosts')(cap(pos)); }); fn('updateSubtotal')(); }
    } catch (e) { warn('fee lookup failed: ' + e.message + '; using the built-in fee table'); }
  }

  document.addEventListener('DOMContentLoaded', function () { if (B()) loadFeePrices(); });
  // The thumbnail is 240 px on a phone and System's preview size above 768 px: redraw the cards when a resize crosses that line (same rule as System).
  if (global.addEventListener) global.addEventListener('resize', function () {
    clearTimeout(global.__ssResizeT);
    global.__ssResizeT = setTimeout(function () {
      var s = bs(); if (lastPreviewSize === null || !s || !isStandard() || previewSize() === lastPreviewSize) return;
      lastPreviewSize = previewSize();
      positions().forEach(function (pos) { var c = s.components && s.components[pos + 'Spokes']; if (c && document.querySelector('#' + pos + '-spokes-subsection .ss-card')) fn('updateComponentUI')(cap(pos), 'Spokes', c); });
    }, 200);
  });

  if (global.addEventListener) global.addEventListener('loam:valve-color', function (e) { setValveColor(e && e.detail); });

  global.StandardStudio = {
    CFG: CFG,
    setValveColor: setValveColor,
    offered: offered,
    refresh: refresh,
    syncCards: syncCards,
    applyColorFee: applyColorFee,
    isDiscountExempt: isDiscountExempt,
    feeCents: feeCents,
    feeCartLines: feeCartLines,
    lineVariantGid: lineVariantGid,
    lineQuantityAvailable: lineQuantityAvailable,
    onListPick: onListPick,
    onSpokesAssigned: onSpokesAssigned,
    decorateComponentUI: decorateComponentUI,
    splitSpokes: splitSpokes,
    summaryDescription: summaryDescription,
    summaryFeeRows: summaryFeeRows,
    readableProperties: readableProperties,
    snapshot: snapshot,
    restore: restore,
    afterStep3Render: afterStep3Render,
    openStudio: openStudio,
    // for tests / console
    _notice: function (pos) { syncNoticeOwner(); return notices[pos] || ''; }, _feeTypeFor: feeTypeFor, _counts: counts, _colorLabel: colorLabel, _paintNamesOf: paintNamesOf, _colorNames: colorNames, _lacing: lacing, _isCustom: isCustom, _lineColor: lineColor, _sharedProduct: sharedProduct, _feeInfoFor: feeInfoFor
  };
})(window);
